import { PropsWithChildren, ReactNode } from 'react';
import { Link } from '@treams/treams-routes';
import { Box, BoxProps, Flex } from 'reflexbox';
import styled from 'styled-components';
import { rgba } from 'polished';

import { ChevronRightIcon } from '@treams/treams-icons';

import { Skeleton } from './Skeleton';

const Container = styled(Box)<{ interactive?: boolean; selected?: boolean }>`
	border-radius: 5px;
	background: ${(props) => props.theme.colors.white};
	box-shadow:
		0px 2px 4px ${(props) => rgba(props.theme.colors.slateBlue, 0.1)},
		0px 1px 2px ${(props) => rgba(props.theme.colors.slateBlue, 0.05)},
		0px 0px 0px 1px ${(props) => rgba(props.theme.colors.slateBlue, 0.25)} inset;
	transition: 0.1s ease-out all;

	${(props) => props.interactive && `cursor: pointer`};

	${(props) =>
		props.interactive &&
		!props.selected &&
		`
		cursor: pointer;

		&:hover {
			box-shadow: 0px 2px 4px ${rgba(props.theme.colors.slateBlue, 0.16)},
				0px 8px 16px ${rgba(props.theme.colors.slateBlue, 0.25)};
			transform: translateY(-2px);
		}

		&:active {
			background: ${rgba(props.theme.colors.white, 0.5)};
		}
		`}

	${(props) =>
		props.selected &&
		`box-shadow: 0px 0px 0px 2px ${props.theme.colors.primary},
					0px 0px 0px 4px ${rgba(props.theme.colors.primary, 0.25)};`}
`;

const Contents = styled(Box)<{ banner?: string }>`
	${(props) =>
		props.banner &&
		`
        border-left: 8px solid ${props.theme.colors[props.banner]};
        border-top-left-radius: 5px;
        border-bottom-left-radius: 5px;
    `}
`;

const CardSkeleton = styled(Box)`
	border-radius: 5px;
	background: white;
	padding: 8px;
`;

const Chevron = styled(Box)`
	width: 24px;

	svg {
		display: block;

		path {
			fill: ${(props) => props.theme.colors.slateBlue};
		}
	}
`;

export const Card = Skeleton(function Card(
	props: PropsWithChildren<
		BoxProps & {
			link?: string;
			selected?: boolean;
			onClick?: () => void;
			header?: ReactNode;
			footer?: ReactNode;
			banner?: string;
			navigationState?: object;
		}
	>,
) {
	const { link, navigationState, ...rest } = props;

	if (link) {
		return (
			<Link
				to={link}
				target={link.startsWith('https://') ? '_blank' : undefined}
				navigationState={navigationState}
			>
				<Container {...rest} interactive>
					{props.header}
					<Contents p={3} banner={props.banner}>
						<Flex alignItems="center">
							<Box flexShrink={1} flexGrow={1}>
								{props.children}
							</Box>

							<Chevron flexGrow={0} flexShrink={0}>
								<ChevronRightIcon />
							</Chevron>
						</Flex>
						{props.footer}
					</Contents>
				</Container>
			</Link>
		);
	}

	return (
		<Container interactive={!!props.onClick} {...rest}>
			{props.header}
			<Contents p={3} banner={props.banner}>
				{props.children}
			</Contents>
		</Container>
	);
}, CardSkeleton);
